/**
 * NotificationCenterService - In-App Notification Center
 * Phase 28: Persistent inbox for trip-related alerts
 * 
 * Manages the notification bell badge count and inbox UI.
 * Fetches notifications from backend, marks as read, shows unread count.
 * Works in both browser and Capacitor native app.
 */

import { authFetch } from './authFetch.js';

const API_URL = import.meta.env?.VITE_BACKEND_URL || 'https://6wdv4xnpei.execute-api.us-west-1.amazonaws.com/prod';

class NotificationCenterService {
    constructor() {
        this.notifications = [];
        this.unreadCount = 0;
        this.userId = null;
        this.loaded = false;
    }

    /**
     * Initialize the notification center for a user.
     * Call after authentication completes.
     * @param {string} userId
     */
    async init(userId) {
        this.userId = userId;
        await this.refresh();
    }

    /**
     * Fetch notifications from backend and update badge count.
     */
    async refresh() {
        if (!this.userId) return;

        try {
            const res = await authFetch(`${API_URL}/api/notifications?userId=${encodeURIComponent(this.userId)}`);
            if (!res.ok) {
                console.warn('🔔 Failed to fetch notifications:', res.status);
                return;
            }

            const data = await res.json();
            if (data.status === 'success') {
                this.notifications = data.notifications || [];
                this.unreadCount = data.unreadCount || 0;
                this.loaded = true;
                this._updateBadge();
            }
        } catch (error) {
            console.warn('🔔 Error fetching notifications:', error);
        }
    }

    /**
     * Mark a single notification as read.
     * @param {string} createdAt - The notification's createdAt timestamp (sort key)
     */
    async markRead(createdAt) {
        if (!this.userId) return;

        try {
            await authFetch(`${API_URL}/api/notifications/read?userId=${encodeURIComponent(this.userId)}&createdAt=${encodeURIComponent(createdAt)}`, {
                method: 'PUT'
            });

            const notif = this.notifications.find(n => n.createdAt === createdAt);
            if (notif && !notif.read) {
                notif.read = true;
                this.unreadCount = Math.max(0, this.unreadCount - 1);
                this._updateBadge();
            }
        } catch (error) {
            console.warn('🔔 Error marking notification read:', error);
        }
    }

    /**
     * Mark all notifications as read.
     */
    async markAllRead() {
        if (!this.userId) return;

        try {
            await authFetch(`${API_URL}/api/notifications/read-all?userId=${encodeURIComponent(this.userId)}`, {
                method: 'PUT'
            });

            this.notifications.forEach(n => n.read = true);
            this.unreadCount = 0;
            this._updateBadge();
        } catch (error) {
            console.warn('🔔 Error marking all read:', error);
        }
    }

    /**
     * Create a notification (called after badge awards, state unlocks, etc.)
     * @param {Object} params - { type, title, body, deepLink? }
     */
    async createNotification({ type, title, body, deepLink }) {
        if (!this.userId) return;

        try {
            await authFetch(`${API_URL}/api/notifications`, {
                method: 'POST',
                body: JSON.stringify({
                    userId: this.userId,
                    type,
                    title,
                    body,
                    deepLink: deepLink || null
                })
            });

            await this.refresh();
        } catch (error) {
            console.warn('🔔 Error creating notification:', error);
        }
    }

    /**
     * Get notifications grouped by Today / Earlier
     */
    getGrouped() {
        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();

        const today = this.notifications.filter(n => n.createdAt >= todayStart);
        const earlier = this.notifications.filter(n => n.createdAt < todayStart);

        return { today, earlier };
    }

    /**
     * Get the icon for a notification type
     */
    getIcon(type) {
        const icons = {
            badge_earned: '🏅',
            state_unlocked: '🗺️',
            wind_advisory: '🌬️',
            fuel_update: '⛽',
            report_confirmed: '✓',
            waypoint_missed: '🔔'
        };
        return icons[type] || '🔔';
    }

    /**
     * Format relative time for display
     */
    formatRelativeTime(isoString) {
        const now = Date.now();
        const then = new Date(isoString).getTime();
        const diffMs = now - then;
        const diffMin = Math.floor(diffMs / 60000);
        const diffHr = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMin < 1) return 'just now';
        if (diffMin < 60) return `${diffMin}m ago`;
        if (diffHr < 24) return `${diffHr}h ago`;
        if (diffDays === 1) return 'yesterday';
        return `${diffDays}d ago`;
    }

    /**
     * Render the notification inbox HTML
     */
    renderInbox() {
        const { today, earlier } = this.getGrouped();

        let html = '<div style="padding: 0.5rem;">';

        // Header with mark all read
        html += '<div style="display: flex; justify-content: space-between; align-items: center; padding: 0.5rem 0.5rem 0.75rem;">';
        html += '<span style="font-weight: 700; font-size: 1rem; color: #2c3e50;">Notifications</span>';
        if (this.unreadCount > 0) {
            html += '<button onclick="window.notificationCenterService.markAllRead().then(() => window.renderNotificationInbox())" style="background: none; border: none; color: #1e88e5; font-size: 0.8rem; cursor: pointer; font-weight: 600;">Mark all read</button>';
        }
        html += '</div>';

        if (this.notifications.length === 0) {
            html += '<div style="text-align: center; padding: 2rem 1rem; color: #90a4ae; font-size: 0.9rem;">No notifications yet</div>';
            html += '</div>';
            return html;
        }

        // Today section
        if (today.length > 0) {
            html += '<div style="font-size: 0.75rem; font-weight: 600; color: #90a4ae; padding: 0.4rem 0.5rem; text-transform: uppercase;">Today</div>';
            today.forEach(n => { html += this._renderNotification(n); });
        }

        // Earlier section
        if (earlier.length > 0) {
            html += '<div style="font-size: 0.75rem; font-weight: 600; color: #90a4ae; padding: 0.4rem 0.5rem; margin-top: 0.5rem; text-transform: uppercase;">Earlier</div>';
            earlier.forEach(n => { html += this._renderNotification(n); });
        }

        html += '</div>';
        return html;
    }

    /**
     * Render a single notification item
     */
    _renderNotification(n) {
        const icon = this.getIcon(n.type);
        const time = this.formatRelativeTime(n.createdAt);
        const unreadStyle = n.read ? '' : 'background: #f0f7ff;';
        const encodedCreatedAt = encodeURIComponent(n.createdAt);

        return `<div onclick="window.notificationCenterService.markRead('${n.createdAt}').then(() => window.renderNotificationInbox())" style="padding: 0.6rem 0.5rem; border-radius: 6px; margin-bottom: 0.25rem; cursor: pointer; ${unreadStyle}">
            <div style="display: flex; gap: 0.5rem; align-items: flex-start;">
                <span style="font-size: 1.1rem;">${icon}</span>
                <div style="flex: 1; min-width: 0;">
                    <div style="font-size: 0.85rem; font-weight: ${n.read ? '400' : '600'}; color: #2c3e50; line-height: 1.3;">${n.title}</div>
                    <div style="font-size: 0.8rem; color: #546e7a; margin-top: 0.15rem; line-height: 1.3;">${n.body}</div>
                </div>
                <span style="font-size: 0.7rem; color: #90a4ae; white-space: nowrap;">${time}</span>
            </div>
        </div>`;
    }

    /**
     * Update the notification badge in the hamburger menu
     */
    _updateBadge() {
        const badge = document.getElementById('notificationBadge');
        if (badge) {
            if (this.unreadCount > 0) {
                badge.textContent = this.unreadCount > 9 ? '9+' : String(this.unreadCount);
                badge.style.display = 'inline-flex';
            } else {
                badge.style.display = 'none';
            }
        }

        const dot = document.getElementById('hamburgerDot');
        if (dot) {
            dot.style.display = this.unreadCount > 0 ? 'block' : 'none';
        }
    }
}

// Export singleton
export const notificationCenterService = new NotificationCenterService();
